import { useState, useEffect, useCallback, useRef } from 'react'
import { KEY_ESCAPE } from 'keycode-js'
import copy from 'copy-to-clipboard'

const isBrowser = typeof window !== 'undefined'

// source: https://usehooks.com/useOnClickOutside/
export function useOnClickOutside(ref, handler) {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) {
        return
      }
      handler(event)
    }

    document.addEventListener('mousedown', listener)
    document.addEventListener('touchstart', listener)

    return () => {
      document.removeEventListener('mousedown', listener)
      document.removeEventListener('touchstart', listener)
    }
  }, [handler, ref])
}

export function useHighlight(value) {
  const [highlighted, highligthOne] = useState(null)
  useEffect(() => {
    if (value) {
      highligthOne(value)
    }
  }, [value])

  return highlighted
}

export function useOpenClose(initialValue = false) {
  const [isOpen, setOpen] = useState(initialValue)

  useEffect(() => {
    const handleEvent = (e) => {
      if (e.keyCode === KEY_ESCAPE) {
        e.preventDefault()
        setOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEvent)
    }
    return () => {
      document.removeEventListener('keydown', handleEvent)
    }
  }, [isOpen])

  return [isOpen, setOpen]
}

export function useOffset() {
  const [offset, setOffset] = useState(null)
  const ref = useCallback((node) => {
    if (node !== null) {
      setOffset({
        left: node.offsetLeft,
        width: node.offsetWidth,
        height: node.offsetHeight,
      })
    }
  }, [])
  return [offset, ref]
}

export function useWhyDidYouUpdate(name, props) {
  const previousProps = useRef()

  useEffect(() => {
    if (previousProps.current) {
      const allKeys = Object.keys({ ...previousProps.current, ...props })

      const changesObj = {}

      allKeys.forEach((key) => {
        if (previousProps.current[key] !== props[key]) {
          changesObj[key] = {
            from: previousProps.current[key],
            to: props[key],
          }
        }
      })

      if (Object.keys(changesObj).length) {
        console.log('[why-did-you-update]', name, changesObj)
      }
    }

    previousProps.current = props
  })
}

export function usePersistedState(key, initialValue = null) {
  const [value, setValue] = useState(() => {
    if (!isBrowser) {
      return initialValue
    }
    const persistedValue = window.localStorage.getItem(key)
    return persistedValue !== null && persistedValue !== 'undefined'
      ? JSON.parse(persistedValue)
      : initialValue
  })

  useEffect(() => {
    window.localStorage.setItem(key, JSON.stringify(value))
  }, [key, value])

  return [value, setValue]
}

export function useBbox() {
  const ref = useRef()
  const [bbox, setBbox] = useState({})

  const set = () =>
    setBbox(ref && ref.current ? ref.current.getBoundingClientRect() : {})

  useEffect(() => {
    set()
    window.addEventListener('resize', set)
    return () => window.removeEventListener('resize', set)
  }, [])

  return [bbox, ref]
}

export function useCopyClipboard(text, options) {
  const [isCopied, setIsCopied] = useState(false)
  const successDuration = options && options.successDuration

  useEffect(() => {
    if (isCopied && successDuration) {
      const id = setTimeout(() => {
        setIsCopied(false)
      }, successDuration)

      return () => {
        clearTimeout(id)
      }
    }
  }, [isCopied, successDuration])

  return [
    isCopied,
    () => {
      const didCopy = copy(text)
      setIsCopied(didCopy)
    },
  ]
}

export function useDelayedState(notDelayedValue, timeout) {
  const [delayedValue, setDelayedValue] = useState(notDelayedValue)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (notDelayedValue) {
        setDelayedValue(true)
      } else {
        setDelayedValue(false)
      }
    }, timeout)
    return () => clearTimeout(timer)
  }, [notDelayedValue, timeout])

  return [delayedValue, setDelayedValue]
}

export function useInterval(callback, delay) {
  const intervalId = useRef(null)
  const savedCallback = useRef(callback)
  useEffect(() => {
    savedCallback.current = callback
  })
  useEffect(() => {
    const tick = () => savedCallback.current()
    if (typeof delay === 'number') {
      intervalId.current = window.setInterval(tick, delay)
      return () => window.clearInterval(intervalId.current)
    }
  }, [delay])
  return intervalId.current
}

export function useTimeout(callback, delay) {
  const savedCallback = useRef()

  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  useEffect(() => {
    function tick() {
      savedCallback.current()
    }
    if (delay !== null) {
      const id = setTimeout(tick, delay)
      return () => clearTimeout(id)
    }
  }, [delay])
}
